import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  exchangeSession: (sessionId) => api.get('/auth/session', {
    headers: { 'X-Session-ID': sessionId }
  }),
  googleAuth: (idToken) => api.post('/auth/google', { id_token: idToken }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
  changePassword: (current_password, new_password, confirm_password) => 
    api.post('/auth/change-password', { current_password, new_password, confirm_password }),
};

// Membership APIs
export const membershipAPI = {
  apply: (data) => api.post('/membership/apply', data),
  getStatus: () => api.get('/membership/status'),
  getApplications: () => api.get('/membership/applications'),
  approve: (userId) => api.post(`/membership/approve/${userId}`),
  reject: (userId) => api.post(`/membership/reject/${userId}`),
};

// Profile APIs
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  updateEmailPreferences: (prefs) => api.patch('/profile/email-preferences', prefs),
  uploadPicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/profile/picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePicture: () => api.delete('/profile/picture'),
};

// Professional profile (member-owned; shared with the Consulate directory)
export const professionalProfileAPI = {
  update: (userId, data) => api.patch(`/members/${userId}/professional-profile`, data),
};

// Consulate directory (consulate role only)
export const consulateAPI = {
  getDirectory: (params) => api.get('/consulate/directory', { params }),
  getMember: (userId) => api.get(`/consulate/directory/${userId}`),
  exportCsv: (params) => api.get('/consulate/directory/export', { params, responseType: 'blob' }),
  changePassword: (current_password, new_password, confirm_password) =>
    api.patch('/consulate/account/password', { current_password, new_password, confirm_password }),
};

// Consulate account management (admin)
export const consulateAccountsAPI = {
  list: () => api.get('/admin/consulate-accounts'),
  create: (data) => api.post('/admin/consulate-accounts', data),
  update: (accountId, data) => api.patch(`/admin/consulate-accounts/${accountId}`, data),
  delete: (accountId) => api.delete(`/admin/consulate-accounts/${accountId}`),
};

// Directory APIs
export const directoryAPI = {
  getMembers: (params) => api.get('/directory', { params }),
  getUniversities: () => api.get('/directory/universities'),
};

// Resources APIs
export const resourcesAPI = {
  getAll: (category) => api.get('/resources', { params: { category } }),
  get: (resourceId) => api.get(`/resources/${resourceId}`),
  getCategories: () => api.get('/resources/categories'),
  create: (data) => api.post('/resources', data),
  update: (resourceId, data) => api.put(`/resources/${resourceId}`, data),
  uploadFile: (resourceId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/resources/${resourceId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (resourceId) => api.delete(`/resources/${resourceId}`),
};

// Events APIs
export const eventsAPI = {
  getAll: (past = false, category = null) =>
    api.get('/events', { params: { past, ...(category ? { category } : {}) } }),
  getById: (eventId) => api.get(`/events/${eventId}`),
  downloadIcs: (eventId) => api.get(`/events/${eventId}/ics`, { responseType: 'blob' }),
  listPhotos: (eventId) => api.get(`/events/${eventId}/photos`),
  uploadPhoto: (eventId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/events/${eventId}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updatePhoto: (eventId, photoId, data) =>
    api.patch(`/events/${eventId}/photos/${photoId}`, data),
  deletePhoto: (eventId, photoId) =>
    api.delete(`/events/${eventId}/photos/${photoId}`),
  create: (data) => api.post('/events', data),
  update: (eventId, data) => api.put(`/events/${eventId}`, data),
  delete: (eventId) => api.delete(`/events/${eventId}`),
  rsvp: (eventId) => api.post(`/events/${eventId}/rsvp`),
  cancelRsvp: (eventId) => api.delete(`/events/${eventId}/rsvp`),
  getRsvps: (eventId) => api.get(`/events/${eventId}/rsvps`),
  getMyRsvp: (eventId) => api.get(`/events/${eventId}/my-rsvp`),
  exportRsvps: (eventId) => api.get(`/events/${eventId}/rsvps/export`, { responseType: 'blob' }),
};

// Unsubscribe + email preferences (public, token-authenticated)
export const unsubscribeAPI = {
  validate: (uid, type, token) => api.get('/unsubscribe/validate', { params: { uid, type, token } }),
  confirm: (uid, type, token) => api.post('/unsubscribe', null, { params: { uid, type, token } }),
};

// Announcements APIs
export const announcementsAPI = {
  getAll: () => api.get('/announcements'),
  getAllAdmin: () => api.get('/announcements/all'),
  create: (data) => api.post('/announcements', data),
  update: (announcementId, data) => api.put(`/announcements/${announcementId}`, data),
  delete: (announcementId) => api.delete(`/announcements/${announcementId}`),
};

// Communications (bulk email) APIs
export const communicationsAPI = {
  previewRecipients: (target) => api.post('/communications/bulk/preview', target),
  send: (data) => api.post('/communications/bulk', data),
  listSent: () => api.get('/communications/bulk'),
  emailGroups: () => api.get('/communications/email-groups'),
  cancelBatch: (batchId) => api.post(`/communications/batches/${batchId}/cancel`),
};

// Mentorship APIs
export const mentorshipAPI = {
  submitInterest: (data) => api.post('/mentorship/interest', data),
  getMyInterest: () => api.get('/mentorship/my-interest'),
  getAllInterests: () => api.get('/mentorship/interests'),
};

// Support APIs
export const supportAPI = {
  submitRequest: (data) => api.post('/support/request', data),
  getRequests: (status) => api.get('/support/requests', { params: { status } }),
  updateStatus: (requestId, status) => api.put(`/support/requests/${requestId}/status`, null, { params: { status } }),
};

// Admin APIs
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getMembers: () => api.get('/admin/members'),
  getMember: (userId) => api.get(`/admin/members/${userId}`),
  updateMember: (userId, data) => api.put(`/admin/members/${userId}`, data),
  updateRole: (userId, role) => api.put(`/admin/members/${userId}/role`, null, { params: { role } }),
  deleteMember: (userId) => api.delete(`/admin/members/${userId}`),
  exportMembers: () => api.get('/admin/export/members'),
  // Dues management
  recordDues: (data) => api.post('/admin/dues', data),
  getMemberDues: (userId) => api.get(`/admin/dues/${userId}`),
  getAllDues: () => api.get('/admin/dues'),
  updateDues: (paymentId, data) => api.put(`/admin/dues/${paymentId}`, data),
  deleteDues: (paymentId) => api.delete(`/admin/dues/${paymentId}`),
  updateDuesStatus: (userId, status) => api.put(`/admin/members/${userId}/dues-status`, null, { params: { status } }),
};

// Executive positions (admin)
export const executivePositionsAPI = {
  list: () => api.get('/admin/executive-positions'),
  assign: (position, userId, transfer = false) =>
    api.put(`/admin/executive-positions/${encodeURIComponent(position)}`, { user_id: userId }, { params: transfer ? { transfer: true } : {} }),
  remove: (position) => api.delete(`/admin/executive-positions/${encodeURIComponent(position)}`),
};

// Dues APIs (member-facing)
export const duesAPI = {
  getMyPayments: () => api.get('/dues/my-payments'),
  getMyStatus: () => api.get('/dues/my-status'),
};

// Executive Workspace APIs
export const workspaceAPI = {
  getNotes: (category) => api.get('/workspace/notes', { params: { category } }),
  createNote: (data) => api.post('/workspace/notes', data),
  updateNote: (noteId, data) => api.put(`/workspace/notes/${noteId}`, data),
  deleteNote: (noteId) => api.delete(`/workspace/notes/${noteId}`),
  // Documents
  getDocuments: (category) => api.get('/workspace/documents', { params: { category } }),
  createDocument: (data) => api.post('/workspace/documents', data),
  uploadDocumentFile: (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/workspace/documents/${docId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateDocument: (docId, data) => api.put(`/workspace/documents/${docId}`, data),
  deleteDocument: (docId) => api.delete(`/workspace/documents/${docId}`),
};

// Governance APIs (AGM + Voting)
export const governanceAPI = {
  getDocuments: (year) => api.get('/governance/agm-documents', { params: { year } }),
  createDocument: (data) => api.post('/governance/agm-documents', data),
  uploadDocumentFile: (agmId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/governance/agm-documents/${agmId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteDocument: (agmId) => api.delete(`/governance/agm-documents/${agmId}`),
  getVotes: (year) => api.get('/governance/votes', { params: { year } }),
  createVote: (data) => api.post('/governance/votes', data),
  castVote: (voteId, option) => api.post(`/governance/votes/${voteId}/cast`, { option }),
  closeVote: (voteId) => api.put(`/governance/votes/${voteId}/close`),
  reopenVote: (voteId) => api.put(`/governance/votes/${voteId}/reopen`),
  deleteVote: (voteId) => api.delete(`/governance/votes/${voteId}`),
};

// Elections APIs
export const electionsAPI = {
  getAll: (year) => api.get('/elections', { params: { year } }),
  create: (data) => api.post('/elections', data),
  updateStatus: (electionId, status) => api.put(`/elections/${electionId}/status`, null, { params: { status } }),
  delete: (electionId) => api.delete(`/elections/${electionId}`),
  nominate: (electionId, data) => api.post(`/elections/${electionId}/nominate`, data),
  deleteNomination: (electionId, nominationId) => api.delete(`/elections/${electionId}/nominations/${nominationId}`),
  vote: (electionId, nominee_user_id) => api.post(`/elections/${electionId}/vote`, { nominee_user_id }),
  getMyVote: (electionId) => api.get(`/elections/${electionId}/my-vote`),
};

// Advanced Mentorship APIs
export const mentorshipMatchAPI = {
  getProfiles: (roleType) => api.get('/mentorship/profiles', { params: { role_type: roleType } }),
  getMyProfile: () => api.get('/mentorship/my-mentorship-profile'),
  createProfile: (data) => api.post('/mentorship/profiles', data),
  updateProfile: (data) => api.put('/mentorship/profiles', data),
  getMatches: () => api.get('/mentorship/matches'),
  getSuggestions: () => api.get('/mentorship/suggestions'),
  adminMatch: (mentorId, menteeId) => api.post('/admin/mentorship/match', null, { params: { mentor_id: mentorId, mentee_id: menteeId } }),
  adminDeleteMatch: (matchId) => api.delete(`/admin/mentorship/match/${matchId}`),
};

export default api;
